import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { ChipColor } from '../chip/chip';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';
import { TooltipDirective } from '../tooltip/tooltip.directive';

export type EntityVisualSize = 'sm' | 'md';

export interface EntityVisualStatus {
  icon: string;
  color: ChipColor;
  /** Translation key for the tooltip and screen-reader label; omit for a plain badge. */
  label?: string | null;
}

export type EntityVisualAddon = Pick<EntityVisualStatus, 'icon' | 'color'>;

@Component({
  selector: 'zag-entity-visual',
  imports: [ZagIcon, TooltipDirective, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (imageUrl()) {
      <img class="zag-entity-visual__image" [src]="imageUrl()!" alt="" aria-hidden="true" />
    } @else if (icon()) {
      <span
        class="zag-entity-visual__icon"
        [class.zag-entity-visual__icon--surface]="hasColoredSurface()"
        [class.zag-icon-surface-gradient]="hasColoredSurface()"
        [style.background-color]="surfaceColor()"
        [style.color]="iconColor()"
        [zagIcon]="icon()!"
        aria-hidden="true"
      ></span>
    }
    @if (status(); as badge) {
      <span
        [class]="'zag-entity-visual__status zag-entity-visual__status--' + badge.color"
        [zagTooltip]="badge.label ? (badge.label | translate) : null"
        tooltipPosition="above"
      >
        <span [zagIcon]="badge.icon" aria-hidden="true"></span>
        @if (badge.label; as label) {
          <span class="zag-sr-only">{{ label | translate }}</span>
        }
      </span>
    }
    @if (addon(); as badge) {
      <span [class]="'zag-entity-visual__status zag-entity-visual__status--top zag-entity-visual__status--' + badge.color">
        <span [zagIcon]="badge.icon" aria-hidden="true"></span>
      </span>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        position: relative;
      }

      :host(.zag-entity-visual--sm) {
        width: var(--entity-visual-size-sm);
        height: var(--entity-visual-size-sm);
      }

      :host(.zag-entity-visual--md) {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
      }

      .zag-entity-visual__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: var(--border-radius-sm);
        border: 1px solid var(--border-color-default);
      }

      .zag-entity-visual__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        color: var(--text-color-secondary);
      }

      .zag-entity-visual__icon--surface {
        border-radius: 50%;
        background-color: var(--color-primary);
      }

      :host(.zag-entity-visual--sm) .zag-entity-visual__icon {
        font-size: var(--icon-size-secondary);
      }

      :host(.zag-entity-visual--sm) .zag-entity-visual__icon--surface {
        font-size: var(--icon-size-compact);
      }

      :host(.zag-entity-visual--md) .zag-entity-visual__icon {
        font-size: var(--entity-avatar-icon-size-list);
      }

      .zag-entity-visual__status {
        position: absolute;
        inset-block-end: calc(var(--spacing-2xs) * -1);
        inset-inline-end: calc(var(--spacing-2xs) * -1);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        inline-size: var(--spacing-md);
        block-size: var(--spacing-md);
        background: color-mix(in srgb, currentColor 18%, var(--surface-color-default));
        border: var(--border-width-default) solid var(--surface-color-default);
        border-radius: var(--border-radius-full);
        font-size: var(--icon-size-compact);
      }

      :host(.zag-entity-visual--sm) .zag-entity-visual__status {
        inline-size: var(--spacing-sm);
        block-size: var(--spacing-sm);
        font-size: 1rem;
      }

      :host(.zag-entity-visual--sm) .zag-entity-visual__status .material-icons {
        font-size: inherit;
      }

      .zag-entity-visual__status--top {
        inset-block-start: calc(var(--spacing-2xs) * -1);
        inset-block-end: auto;
      }

      .zag-entity-visual__status--success {
        color: var(--color-success-foreground, var(--color-success));
      }

      .zag-entity-visual__status--warning {
        color: var(--color-warning-foreground, var(--color-warning));
      }

      .zag-entity-visual__status--error {
        color: var(--color-error-foreground, var(--color-error));
      }

      .zag-entity-visual__status--info {
        color: var(--color-info-foreground, var(--color-info));
      }

      .zag-entity-visual__status--brand {
        color: var(--color-primary);
      }

      .zag-entity-visual__status--neutral {
        color: var(--text-color-secondary);
      }
    `,
  ],
  host: {
    '[class.zag-entity-visual--sm]': 'size() === "sm"',
    '[class.zag-entity-visual--md]': 'size() === "md"',
  },
})
export class EntityVisualComponent {
  private readonly colorContrast = inject(ColorContrastService);

  readonly icon = input<string | null | undefined>(undefined);
  readonly color = input<string | null | undefined>(undefined);
  readonly imageUrl = input<string | null | undefined>(undefined);
  readonly size = input<EntityVisualSize>('sm');
  readonly status = input<EntityVisualStatus | null>(null);
  readonly addon = input<EntityVisualAddon | null>(null);

  protected readonly hasColoredSurface = computed(() => !!this.icon());
  protected readonly surfaceColor = computed(() => this.color() ?? null);
  protected readonly iconColor = computed(() => (this.icon() ? this.colorContrast.getIconColor(this.color()) : null));
}
